import { apiFetch } from './client'

export type AgentState = 'active' | 'stale' | 'error'
export type VersionRelation = 'up' | 'behind' | 'ahead' | 'unknown'

export interface RawHealthAgent {
  agentId: string
  role: string
  engine: string
  model?: string
  style?: string
  pid: number
  startedAt: string
  heartbeatAt?: string | null
  lastActivityAt: string | null
  lastError: { at: string; message: string } | null
  serviceStatus: string
  stalenessMs: number | null
  heartbeatStalenessMs?: number | null
  status: AgentState
  statusReason?: string
  version: string
  versionState?: {
    rel: VersionRelation
    n: number
    commit: string
    label: string
  }
  mode?: string
  project?: string
  sprint?: string
  cwd?: string
  telegramUsername: string | null
  telegramUrl: string | null
}

export interface RawHealthDto {
  updatedAt: string
  agents: RawHealthAgent[]
}

export interface AgentHealth {
  name: string
  role: string
  engine: string
  model: string
  style: string
  pid: number
  state: AgentState
  serviceUp: boolean
  activityMin: number | null
  heartbeatMin: number | null
  statusReason: string
  version: {
    rel: VersionRelation
    n: number
    commit: string
    label: string
  }
  error: string | null
  uptime: string
  mode: string
  project: string
  sprint: string
  cwd: string
  tgUsername: string | null
  telegramUrl: string | null
  raw: RawHealthAgent
}

export interface AgentsHealthResult {
  updatedAt: string
  agents: AgentHealth[]
}

export interface AgentLogs {
  agentId: string
  limit: number
  unit: string
  text: string
  scrubbed: boolean
}

export interface RestartResult {
  agentId: string
  result: 'restarted' | 'failed'
  serviceStatus: string
  message: string
}

export interface AgentGitActionResult {
  agentId: string
  action: 'release' | 'pull'
  result: 'ok' | 'failed'
  message: string
  output: string
  versionState: AgentHealth['version']
}

export interface AgentModelOptions {
  agentId: string
  current: string
  models: string[]
  modelItems?: AgentModelItem[]
}

export interface AgentModelItem {
  id: string
  label: string
  description: string
  isCurrent: boolean
  isDefault: boolean
}

export interface AgentModelDiff {
  agentId: string
  current: string
  next: string
  validation: { ok: boolean; message: string }
  diff: string
  canConfirm: boolean
}

export interface AgentModelConfirm {
  agentId: string
  model: string
  written: true
  atomic: true
  restartRequired: true
}

export interface AgentStyleOption {
  id: string
  label: string
  description: string
}

export interface AgentStyleOptions {
  agentId: string
  current: string
  styles: AgentStyleOption[]
}

export interface AgentStyleDiff {
  agentId: string
  current: string
  next: string
  validation: { ok: boolean; message: string }
  diff: string
  canConfirm: boolean
}

export interface AgentStyleConfirm {
  agentId: string
  style: string
  written: true
  atomic: true
  restartRequired: true
}

export interface AgentEngineOption {
  id: string
  label: string
  models: string[]
  modelItems?: AgentModelItem[]
  recommendedModel: string
}

export interface AgentEngineOptions {
  agentId: string
  current: string
  currentModel: string
  engines: AgentEngineOption[]
}

export interface AgentEngineDiff {
  agentId: string
  current: string
  currentModel: string
  next: string
  nextModel: string
  validation: { ok: boolean; message: string }
  diff: string
  canConfirm: boolean
  restartRequired: true
}

export interface AgentEngineConfirm {
  agentId: string
  engine: string
  model: string
  written: true
  atomic: true
  restartRequired: true
}

export interface GroupProjectEntry {
  chatId: string
  projects: string[]
}

export interface AgentGroupConfig {
  agentId: string
  envFile: string
  allowedGroupChatIds: string[]
  groupProjects: GroupProjectEntry[]
  allowedUserIds: number[]
  ownerUserIds: number[]
}

export interface AgentGroupInput {
  allowedGroupChatIds: string[]
  groupProjects: GroupProjectEntry[]
  allowedUserIds: number[]
  ownerUserIds: number[]
}

export interface AgentGroupDiff {
  agentId: string
  validation: { ok: boolean; message: string }
  diff: string
  canConfirm: boolean
}

export interface AgentGroupConfirm {
  agentId: string
  written: true
  atomic: true
  restartRequired: true
}

export async function fetchAgentsHealth(): Promise<AgentsHealthResult> {
  const dto = await apiFetch<RawHealthDto>('/api/agents/health')
  return {
    updatedAt: dto.updatedAt,
    agents: dto.agents.map(adaptAgent),
  }
}

export function fetchAgentLogs(agentId: string, limit = 50): Promise<AgentLogs> {
  return apiFetch<AgentLogs>(`/api/agents/${encodeURIComponent(agentId)}/logs`, {
    query: { limit },
  })
}

export function restartAgent(agentId: string): Promise<RestartResult> {
  return apiFetch<RestartResult>(`/api/agents/${encodeURIComponent(agentId)}/restart`, {
    method: 'POST',
  })
}

export function releaseAgent(agentId: string): Promise<AgentGitActionResult> {
  return apiFetch<AgentGitActionResult>(`/api/agents/${encodeURIComponent(agentId)}/release`, {
    method: 'POST',
  })
}

export function pullAgent(agentId: string): Promise<AgentGitActionResult> {
  return apiFetch<AgentGitActionResult>(`/api/agents/${encodeURIComponent(agentId)}/pull`, {
    method: 'POST',
  })
}

export function fetchAgentModels(agentId: string): Promise<AgentModelOptions> {
  return apiFetch<AgentModelOptions>(`/api/agents/${encodeURIComponent(agentId)}/models`)
}

export function diffAgentModel(agentId: string, model: string): Promise<AgentModelDiff> {
  return apiFetch<AgentModelDiff>(`/api/agents/${encodeURIComponent(agentId)}/model/diff`, {
    method: 'POST',
    json: { model },
  })
}

export function confirmAgentModel(agentId: string, model: string): Promise<AgentModelConfirm> {
  return apiFetch<AgentModelConfirm>(`/api/agents/${encodeURIComponent(agentId)}/model/confirm`, {
    method: 'POST',
    json: { model },
  })
}

export function fetchAgentStyles(agentId: string): Promise<AgentStyleOptions> {
  return apiFetch<AgentStyleOptions>(`/api/agents/${encodeURIComponent(agentId)}/styles`)
}

export function diffAgentStyle(agentId: string, style: string): Promise<AgentStyleDiff> {
  return apiFetch<AgentStyleDiff>(`/api/agents/${encodeURIComponent(agentId)}/style/diff`, {
    method: 'POST',
    json: { style },
  })
}

export function confirmAgentStyle(agentId: string, style: string): Promise<AgentStyleConfirm> {
  return apiFetch<AgentStyleConfirm>(`/api/agents/${encodeURIComponent(agentId)}/style/confirm`, {
    method: 'POST',
    json: { style },
  })
}

export function fetchAgentEngines(agentId: string): Promise<AgentEngineOptions> {
  return apiFetch<AgentEngineOptions>(`/api/agents/${encodeURIComponent(agentId)}/engines`)
}

export function diffAgentEngine(agentId: string, engine: string, model?: string): Promise<AgentEngineDiff> {
  return apiFetch<AgentEngineDiff>(`/api/agents/${encodeURIComponent(agentId)}/engine/diff`, {
    method: 'POST',
    json: { engine, model },
  })
}

export function confirmAgentEngine(agentId: string, engine: string, model?: string): Promise<AgentEngineConfirm> {
  return apiFetch<AgentEngineConfirm>(`/api/agents/${encodeURIComponent(agentId)}/engine/confirm`, {
    method: 'POST',
    json: { engine, model },
  })
}

export function fetchAgentGroup(agentId: string): Promise<AgentGroupConfig> {
  return apiFetch<AgentGroupConfig>(`/api/agents/${encodeURIComponent(agentId)}/group`)
}

export function diffAgentGroup(agentId: string, input: AgentGroupInput): Promise<AgentGroupDiff> {
  return apiFetch<AgentGroupDiff>(`/api/agents/${encodeURIComponent(agentId)}/group/diff`, {
    method: 'POST',
    json: input,
  })
}

export function confirmAgentGroup(agentId: string, input: AgentGroupInput): Promise<AgentGroupConfirm> {
  return apiFetch<AgentGroupConfirm>(`/api/agents/${encodeURIComponent(agentId)}/group/confirm`, {
    method: 'POST',
    json: input,
  })
}

function adaptAgent(raw: RawHealthAgent): AgentHealth {
  const activityMin = raw.stalenessMs == null ? null : Math.max(0, Math.floor(raw.stalenessMs / 60_000))
  const heartbeatMin = raw.heartbeatStalenessMs == null ? null : Math.max(0, Math.floor(raw.heartbeatStalenessMs / 60_000))
  return {
    name: raw.agentId,
    role: raw.role,
    engine: raw.engine,
    model: raw.model || inferModel(raw.engine),
    style: raw.style || 'normal',
    pid: raw.pid,
    state: raw.status,
    serviceUp: raw.serviceStatus === 'active',
    activityMin,
    heartbeatMin,
    statusReason: raw.statusReason || fallbackStatusReason(raw, activityMin, heartbeatMin),
    version: {
      rel: raw.versionState?.rel ?? 'unknown',
      n: raw.versionState?.n ?? 0,
      commit: raw.versionState?.commit ?? raw.version ?? 'unknown',
      label: raw.versionState?.label ?? (raw.version ? `transport ${raw.version}` : 'unknown'),
    },
    error: raw.lastError?.message ?? null,
    uptime: formatUptime(raw.startedAt),
    mode: raw.mode || 'act',
    project: raw.project || 'unknown',
    sprint: raw.sprint || 'unknown',
    cwd: raw.cwd || 'unknown',
    tgUsername: raw.telegramUsername,
    telegramUrl: raw.telegramUrl,
    raw,
  }
}

function fallbackStatusReason(raw: RawHealthAgent, activityMin: number | null, heartbeatMin: number | null): string {
  if (raw.serviceStatus !== 'active') return `systemd ${raw.serviceStatus || 'unknown'}`
  if (raw.status === 'error' && raw.lastError) return `last error ${raw.lastError.at}`
  if (raw.status === 'stale') return heartbeatMin == null ? 'heartbeat unknown' : `heartbeat stale ${heartbeatMin}m`
  if (activityMin == null) return 'process alive; no successful activity yet'
  return `process alive; last activity ${activityMin}m ago`
}

function inferModel(engine: string): string {
  if (engine.includes('codex')) return 'gpt-5-codex'
  if (engine.includes('claude')) return 'claude-sonnet'
  return 'unknown'
}

function formatUptime(startedAt: string): string {
  const ts = Date.parse(startedAt)
  if (!Number.isFinite(ts)) return 'unknown'
  const minutes = Math.max(0, Math.floor((Date.now() - ts) / 60_000))
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days}д ${hours}ч`
  if (hours > 0) return `${hours}ч ${mins}м`
  return `${mins}м`
}
